import type { HiType } from '../constants';
import { CURENTIA_ASSIGNER, DOCUMENT_TYPES } from './codes';
import { hiTypeOf } from './check';
import { narrativeToText, readPrescription, readWellnessRecord } from './read';
import { readingText } from './readings';
import type { Bundle, Composition, Identifier, Organization, Patient, Practitioner, Reference } from './types';

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const ACCOUNT_ID = /^[a-f0-9]{24}$/;

/** A Patient's details as screens, forms and the record engine use them. */
export interface PatientDetails {
  name: string;
  gender?: NonNullable<Patient['gender']>;
  birthDate?: string;
  mobile?: string;
  abhaNumber?: string;
  abhaAddress?: string;
  /** The patient's Curentia account, when the practice linked it. */
  accountId?: string;
  /** The practice's own record number. */
  medicalRecord?: { value: string; assigner: string };
}

const GENDERS = ['male', 'female', 'other', 'unknown'] as const;

/** Reads what a practice keeps about a patient from a FHIR Patient. Anything malformed is left out. */
export function patientDetails(resource: unknown): PatientDetails {
  const patient = isObject(resource) ? (resource as Partial<Patient>) : {};
  const identifiers = (Array.isArray(patient.identifier) ? patient.identifier : []).filter(isObject) as Identifier[];
  const typed = (code: string) => identifiers.filter((i) => Array.isArray(i.type?.coding) && i.type.coding.some((c) => c?.code === code));
  const text = (value: unknown) => (typeof value === 'string' && value.trim().length > 0 ? value : undefined);
  const abha = typed('ABHA');
  const abhaAddress = text(abha.find((i) => typeof i.value === 'string' && i.value.includes('@'))?.value);
  const abhaNumber = text(abha.find((i) => typeof i.value === 'string' && !i.value.includes('@'))?.value);
  const accountId = typed('PI').find((i) => i.assigner?.display === CURENTIA_ASSIGNER && typeof i.value === 'string' && ACCOUNT_ID.test(i.value))?.value;
  const record = typed('MR')[0];
  const telecom = Array.isArray(patient.telecom) ? patient.telecom.filter(isObject) : [];
  const mobile = text(telecom.find((t) => t.system === 'phone')?.value);
  const name = Array.isArray(patient.name) ? text(patient.name[0]?.text) : undefined;
  const gender = GENDERS.find((g) => g === patient.gender);
  return {
    name: name ?? 'Unnamed patient',
    ...(gender ? { gender } : {}),
    ...(text(patient.birthDate) ? { birthDate: patient.birthDate as string } : {}),
    ...(mobile ? { mobile } : {}),
    ...(abhaNumber ? { abhaNumber } : {}),
    ...(abhaAddress ? { abhaAddress } : {}),
    ...(accountId ? { accountId } : {}),
    ...(record && text(record.value) ? { medicalRecord: { value: record.value as string, assigner: record.assigner?.display ?? '' } } : {}),
  };
}

/** The Curentia account a FHIR Patient is linked to, if any. */
export function patientAccountId(resource: unknown): string | undefined {
  return patientDetails(resource).accountId;
}

/**
 * A document's own date as an ISO time: its Composition's date, which says
 * when it was written, or else the bundle's timestamp. Undefined when neither
 * is a date.
 */
export function documentDateOf(resource: unknown): string | undefined {
  if (!isObject(resource)) return undefined;
  const first = Array.isArray(resource.entry) && isObject(resource.entry[0]) ? resource.entry[0].resource : undefined;
  const composed = isObject(first) && first.resourceType === 'Composition' ? first.date : undefined;
  for (const raw of [composed, resource.timestamp]) {
    if (typeof raw !== 'string') continue;
    const at = Date.parse(raw);
    if (!Number.isNaN(at)) return new Date(at).toISOString();
  }
  return undefined;
}

/** What a timeline shows for one document. */
export interface DocumentSummary {
  hiType: HiType | null;
  title: string;
  date: string;
  /** The first author's name. */
  author?: string;
  /** The organisation that keeps the document. */
  custodian?: string;
  /** Short lines, such as each reading or each medicine. */
  lines: string[];
}

function nameOf(resource: unknown): string | undefined {
  if (!isObject(resource)) return undefined;
  if (resource.resourceType === 'Organization') return (resource as unknown as Organization).name;
  const names = (resource as Partial<Patient | Practitioner>).name;
  return Array.isArray(names) ? names[0]?.text : undefined;
}

const MAX_LINES = 12;

/**
 * Summarises any document for a timeline. Prescriptions list their diagnoses
 * and medicines, wellness records their readings, and other documents their
 * section titles and narrative. Text from other systems is returned as plain
 * text, never as HTML.
 */
export function summarizeDocument(bundle: Bundle): DocumentSummary {
  const entries = Array.isArray(bundle.entry) ? bundle.entry : [];
  const composition = entries[0]?.resource?.resourceType === 'Composition' ? (entries[0].resource as Composition) : undefined;
  const byUrl = new Map(entries.flatMap((entry) => (entry.fullUrl && entry.resource ? [[entry.fullUrl, entry.resource] as const] : [])));
  const resolve = (ref: Reference | undefined) => (ref?.reference ? byUrl.get(ref.reference) : undefined);
  const hiType = hiTypeOf(bundle);
  const base = {
    hiType,
    title: composition?.title ?? (hiType ? DOCUMENT_TYPES[hiType].title : 'Health record'),
    date: documentDateOf(bundle) ?? '',
    ...(nameOf(resolve(composition?.author?.[0])) ? { author: nameOf(resolve(composition?.author?.[0])) as string } : {}),
    ...(nameOf(resolve(composition?.custodian)) ? { custodian: nameOf(resolve(composition?.custodian)) as string } : {}),
  };
  try {
    if (hiType === 'Prescription') {
      const view = readPrescription(bundle);
      const lines = [
        ...view.diagnoses.map((diagnosis) => `Diagnosis: ${diagnosis}`),
        ...view.medicines.map((medicine) => (medicine.dosage ? `${medicine.name}, ${medicine.dosage}` : medicine.name)),
        ...(view.notes ? [view.notes] : []),
        ...(view.advice ? [`Advice: ${view.advice}`] : []),
        ...(view.followUp ? [`Follow-up: ${[view.followUp.date, view.followUp.note].filter(Boolean).join('. ')}`] : []),
      ];
      return { ...base, lines: lines.slice(0, MAX_LINES) };
    }
    if (hiType === 'WellnessRecord') {
      return { ...base, lines: readWellnessRecord(bundle).readings.map(readingText).slice(0, MAX_LINES) };
    }
  } catch {
    // A document this version cannot read falls back to its sections below.
  }
  const lines: string[] = [];
  for (const section of composition?.section ?? []) {
    if (section.title) lines.push(section.title);
    if (section.text?.div) lines.push(...narrativeToText(section.text.div).split('\n').filter((line) => line.trim().length > 0));
  }
  return { ...base, lines: lines.slice(0, MAX_LINES) };
}
